import { normalizeRevisionFamilyKeyPart } from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'
import { compareRevisionCodes } from 'features/knowledgeDocumentViewer/utils/revisionHelpers'
import {
  RolodexEntryQueryBody,
  useRolodexEntries,
} from 'features/rolodex/hooks/useRolodexEntries'
import { useMemo } from 'react'
import { RolodexEntryType } from 'types'
import { z } from 'zod'

// Hand-written zod mirror of the `fim-revision-history` Rolodex entry `data`
// contract (power-pipelines audit of P&ID revisions declared on-sheet but
// missing from uploads → fim-service upserts per family). Source of truth:
// fim-service models; validated at the edge (null-tolerant, open-string enums)
// so an unshipped/evolving backend degrades to "no signal" instead of crashing.
// TODO(fim-revision-history): extract to a dedicated fim/ runtime module + mirror
// the inferred types into src/features/fim/types.ts once the contract stabilizes.
const RevisionHistoryFindingSchema = z.object({
  revision_key: z.string().nullish(),
  raw_label: z.string().nullish(),
  raw_date: z.string().nullish(),
  status: z.string().nullish(),
  rule_id: z.string().nullish(),
  evidence_status: z.string().nullish(),
  available_refs: z
    .array(
      z.object({
        document_id: z.string().nullish(),
        page_id: z.string().nullish(),
        revision_code: z.string().nullish(),
      })
    )
    .nullish(),
  evidence_json: z
    .array(
      z.object({
        page_id: z.string().nullish(),
        document_id: z.string().nullish(),
        table_id: z.string().nullish(),
        row_index: z.number().nullish(),
        bbox: z.array(z.number()).nullish(),
        raw_text: z.string().nullish(),
        raw_date: z.string().nullish(),
        description: z.string().nullish(),
      })
    )
    .nullish(),
})

export type RevisionHistoryFinding = z.infer<
  typeof RevisionHistoryFindingSchema
>

const FimRevisionHistoryDataSchema = z.object({
  site_id: z.string().nullish(),
  issuer_drawing_number: z.string().nullish(),
  sheet_number: z.string().nullish(),
  audit_state: z.string().nullish(),
  declared_count: z.number().nullish(),
  available_count: z.number().nullish(),
  missing_count: z.number().nullish(),
  member_count: z.number().nullish(),
  family_key: z.string().nullish(),
  needs_review_count: z.number().nullish(),
  owner_resolution: z.string().nullish(),
  inventory_in_scope: z.boolean().nullish(),
  table_scan_complete: z.boolean().nullish(),
  inventory_search_complete: z.boolean().nullish(),
  prior_lineage_present: z.boolean().nullish(),
  generation_id: z.string().nullish(),
  findings_unavailable: z.boolean().nullish(),
  findings: z.array(RevisionHistoryFindingSchema).nullish(),
})

export type FimRevisionHistoryData = z.infer<
  typeof FimRevisionHistoryDataSchema
>

/** The audit's family verdict value forecast renders a badge for in v1. */
export const AUDIT_STATE_DECLARED_MISSING = 'declared_missing'
export const AUDIT_STATE_UNDETERMINED = 'undetermined'
export const AUDIT_STATE_NEEDS_REVIEW = 'needs_review'
export const EVIDENCE_STATUS_NEEDS_REVIEW = 'needs_review'

export enum RevisionAuditBadgeMode {
  Missing = 'missing',
  Undetermined = 'undetermined',
  NeedsReview = 'needs_review',
}

/** Which badge treatment (if any) an audit_state gets. Open string tolerant. */
export function revisionAuditBadgeMode(
  auditState: string | null
): RevisionAuditBadgeMode | null {
  if (auditState === AUDIT_STATE_DECLARED_MISSING)
    return RevisionAuditBadgeMode.Missing
  if (auditState === AUDIT_STATE_UNDETERMINED)
    return RevisionAuditBadgeMode.Undetermined
  if (auditState === AUDIT_STATE_NEEDS_REVIEW)
    return RevisionAuditBadgeMode.NeedsReview
  return null
}

export function parseFimRevisionHistoryData(
  data: unknown
): FimRevisionHistoryData | undefined {
  const result = FimRevisionHistoryDataSchema.safeParse(data)
  return result.success ? result.data : undefined
}

export type FindingDisplay = {
  label: string
  date: string | null
  description: string | null
  status: string | null
  /** First usable available-doc ref (only for `available` findings), for the jump-link. */
  availableRef: { documentId: string; pageNum: number | undefined } | null
}

/** Parses the trailing page number from a `DOC-…_pg_{n}` page id. */
export function parsePageNumFromPageId(
  pageId: string | null | undefined
): number | undefined {
  if (!pageId) return undefined
  const match = /_pg_(\d+)$/.exec(pageId)
  return match ? Number(match[1]) : undefined
}

/** First value that is non-blank after trim, returned trimmed; else null. */
export function firstNonEmpty(
  ...values: readonly (string | null | undefined)[]
): string | null {
  for (const value of values) {
    const trimmed = value?.trim()
    if (trimmed) return trimmed
  }
  return null
}

/** First available_ref with a usable (non-blank) document_id, else null. */
function firstUsableAvailableRef(
  refs: RevisionHistoryFinding['available_refs']
): FindingDisplay['availableRef'] {
  for (const ref of refs ?? []) {
    const documentId = firstNonEmpty(ref.document_id)
    if (documentId)
      return { documentId, pageNum: parsePageNumFromPageId(ref.page_id) }
  }
  return null
}

/**
 * Flattens a finding to display fields. The label falls back raw_label →
 * evidence raw_text → revision_key; date and description are the first non-blank
 * value across all `evidence_json` rows (date prefers top-level `raw_date`).
 * `availableRef` is the first usable available ref, only for `available` findings.
 */
export function findingDisplay(
  finding: RevisionHistoryFinding
): FindingDisplay {
  const evidence = finding.evidence_json ?? []
  return {
    label:
      firstNonEmpty(
        finding.raw_label,
        ...evidence.map((e) => e.raw_text),
        finding.revision_key
      ) ?? '',
    date: firstNonEmpty(finding.raw_date, ...evidence.map((e) => e.raw_date)),
    description: firstNonEmpty(...evidence.map((e) => e.description)),
    status: firstNonEmpty(finding.status),
    availableRef:
      finding.status === 'available'
        ? firstUsableAvailableRef(finding.available_refs)
        : null,
  }
}

/** Findings the backend flagged as transcribed-but-unverified (neutral state). */
export function needsReviewFindings(
  finding: FimRevisionHistoryData | undefined
): RevisionHistoryFinding[] {
  return (finding?.findings ?? []).filter(
    (f) => f.evidence_status === EVIDENCE_STATUS_NEEDS_REVIEW
  )
}

export type EvidenceRowDisplay = {
  label: string
  date: string | null
  description: string | null
}

/**
 * Flattens EACH `evidence_json` row of a finding WITHOUT collapsing across rows.
 * For needs_review findings the multiple rows (same label, conflicting dates in
 * different tables) ARE the unresolved conflict and must all render. Falls back
 * to one synthetic row from the finding when `evidence_json` is empty.
 */
export function findingEvidenceRows(
  finding: RevisionHistoryFinding
): EvidenceRowDisplay[] {
  const evidence = finding.evidence_json ?? []
  if (evidence.length === 0) {
    return [
      {
        label: firstNonEmpty(finding.raw_label, finding.revision_key) ?? '',
        date: firstNonEmpty(finding.raw_date),
        description: null,
      },
    ]
  }
  return evidence.map((row) => ({
    label:
      firstNonEmpty(row.raw_text, finding.raw_label, finding.revision_key) ??
      '',
    date: firstNonEmpty(row.raw_date, finding.raw_date),
    description: firstNonEmpty(row.description),
  }))
}

/**
 * Collapses EXACT duplicate rows (same label, date, and description) while
 * preserving genuinely different rows — same revision label with conflicting
 * dates/descriptions differs in the tuple and still renders, so the unresolved
 * conflict remains the signal. Keeps first-seen order (already newest-first via
 * sortFindingsLatestFirst upstream).
 */
export function dedupeEvidenceRows(
  rows: readonly EvidenceRowDisplay[]
): EvidenceRowDisplay[] {
  const seen = new Set<string>()
  const out: EvidenceRowDisplay[] = []
  for (const row of rows) {
    const key = `${row.label}\u0000${row.date ?? ''}\u0000${row.description ?? ''}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(row)
  }
  return out
}

/**
 * Orders findings newest-first by revision code, reusing `compareRevisionCodes`
 * (numbers rank above letters, numerics numerically) — the same ordering the
 * revision-family dropdown uses. Sorts on the clean `revision_key` (not the raw
 * OCR `raw_date`, whose format is not guaranteed). Returns a sorted copy.
 */
export function sortFindingsLatestFirst(
  findings: readonly RevisionHistoryFinding[]
): RevisionHistoryFinding[] {
  return [...findings].sort(
    (a, b) =>
      -compareRevisionCodes(
        firstNonEmpty(a.revision_key, a.raw_label) ?? '',
        firstNonEmpty(b.revision_key, b.raw_label) ?? ''
      )
  )
}

type RolodexEntryLike = { type: string; data: unknown }

/**
 * Picks the family-history entry for a specific sheet from the entries returned
 * by a drawing-number query (one drawing can have several sheets → several
 * family entries). Matches on the viewer's own normalized sheet key (trim +
 * uppercase, '' when absent), per the cross-repo contract (match on raw tags,
 * normalize client-side). Pure + exported for unit testing.
 */
export function selectRevisionHistoryForSheet(
  entries: readonly RolodexEntryLike[],
  sheetNumber: string | undefined
): FimRevisionHistoryData | undefined {
  const target = normalizeRevisionFamilyKeyPart(sheetNumber ?? '')
  for (const entry of entries) {
    if (entry.type !== RolodexEntryType.REVISION_HISTORY) continue
    const data = parseFimRevisionHistoryData(entry.data)
    if (!data) continue
    if (normalizeRevisionFamilyKeyPart(data.sheet_number ?? '') !== target)
      continue
    return data
  }
  return undefined
}

export type UseRevisionHistoryArgs = {
  siteId: string | undefined
  drawingNumber: string | undefined
  sheetNumber: string | undefined
  enabled?: boolean
}

/**
 * Loads the `fim-revision-history` finding for the current page's family and
 * exposes its `audit_state`. Queries by raw `site` + `issuer_drawing_number`
 * tags (AND-only), then selects the matching sheet client-side.
 */
export function useRevisionHistoryForDocument({
  siteId,
  drawingNumber,
  sheetNumber,
  enabled = true,
}: UseRevisionHistoryArgs): {
  finding: FimRevisionHistoryData | undefined
  auditState: string | null
  isLoading: boolean
} {
  const query = useMemo<RolodexEntryQueryBody | undefined>(() => {
    if (!enabled || !siteId || !drawingNumber) return undefined
    return {
      type: RolodexEntryType.REVISION_HISTORY,
      tags: { site: [siteId], issuer_drawing_number: [drawingNumber] },
    }
  }, [enabled, siteId, drawingNumber])

  const { entries, isLoading } = useRolodexEntries(query)

  const finding = useMemo(
    () => selectRevisionHistoryForSheet(entries, sheetNumber),
    [entries, sheetNumber]
  )

  return { finding, auditState: finding?.audit_state ?? null, isLoading }
}
